define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/store/Memory",
	"underscore",
	"csi",
	"csi/action/Controller",
	"csi/action/FormData",
	"csi/comm/PostRequest",
	"csi/config",
	"csi/locale",
	"csi/reflection",
	"csi/store/Nodes",
	"csi/wizard/ActionControllerMixin",
	"csi/xml",
	"csi/xml/Query",
	"./util",

	"csi/table/TypeSwitchboardTable",
	"./DataSheetByProduct"
], function(declare,lang,Memory,_,csi,Controller,FormData,PostRequest,config,locale,reflection,Nodes,ActionControllerMixin,xml,Query,util) {

	var FOOD = "Food";
	var STYLE = "Style";
	var MATERIAL = "Material";
	let FORMULATED_MATERIAL = "FormulatedMaterial";
	let PROJECT = "Project";

	/**
	 * If a datasheet, then can aggregate if you have edit access; if no datasheet, then can aggregate only if you have create access
	 * @param {Context} ctx
	 * @param {FormData} fd
	 * @return {boolean}
	 */
	function _canAggregate(ctx, fd) {
		var master = ctx.getNode();
		var revision = ctx.cache.getNode(master.CurrentRevision);
		if (revision) {
			return xml.nodeHasAccess(revision, xml.RIGHT_WRITE);
		}

		// if no data sheet exists, then only return true if you have the appropriate role to create a spec data sheet;
		// DataSheetByProduct view will flag node with NOEDIT if no such access
		return !xml.nodeHasFlag(master, xml.NODE_FLAG_NOEDIT);
	}

	return declare([Controller, ActionControllerMixin], {
		/** @property {FormData} _fdProducts - form data to collect product selections */
		_fdProducts: null,

		/** @property {string[]} _modifiedUrls - list of urls modified by operation */
		_modifiedUrls: null,

		/**
		 * Change controller to be in context of section node rather than client group node
		 */
		constructor: function() {
			if (this.m_urlNode) {
				// this is needed because the group node URL does not match the section url
				const qrGroup = this.widget.getQueryResultByDomNode(this.refNode || this.m_element);
				if (qrGroup && qrGroup.isGroup()) {
					this.origUrlNode = this.m_urlNode;
					this.m_urlNode = qrGroup.groupValue;
				}
			} else {
				this.m_urlNode = this.m_urlParent;
			}
		},

		/**
		 * NOTE: Push only works on shared sections for now, so below code is not used
		 */
		onCreatePopupParams: function(params) {
			if (!util.isSectionShared(this.getContext())) {
				params.dialogActionCheckboxes =  [{
					id: "SpecificationSectionPushWizardCheckboxLabel",
					pageIndex: 1
				}];
			}
		},

		/**
		 * Initialize form data for each page
		 */
		onNextPage: function(wizard, currentPage, nextPage, bFinish) {
			var index = wizard.getIndexOfPage(nextPage);
			var fd = this.getFormData();

			if (index === 0) {
				fd = this._fdProducts;
				if (!fd) {
					fd = this._fdProducts = new FormData(this);
				}
			} else if (!bFinish) {
				fd.resetSelections();
			}

			fd.m_bRequireOne = true;

			return bFinish ? null : wizard.setFormDataToPage(nextPage, fd, (index == 1));
		},

		/**
		 * Alter query for data sheets based on the selected products that do not already have this shared section
		 */
		getWizardViewQuery: function(wizard, view, index) {
			if (index == 1) {
				var productUrls = this._fdProducts.getSelectedValue();
				var q = new Query();

				_.each(productUrls, function(productUrl) {
					this.RefAttr(xml.ATTR_PARENT, xml.OP_EQUALS, productUrl);
				}, q.Or());

				// also set the productStore so it knows which groups to display
				view.set("productStore", this._getProductStore());

				return q;
			}

			return null;
		},

		/**
		 * Need to check for edit access on target datasheets, or role access on target products
		 */
		onFormData: function(fd) {
			fd.m_fAggregateTest = _canAggregate;
		},

		/**
		 * Prevent datasheets that contain section from being selected
		 */
		onWizardViewPreCreate: function(wizard, page, defView, viewParams) {
			var index = wizard.getIndexOfPage(page);
			var self = this;

			if (index == 0) {
				// get parent node type to initialize the toolbar switchboard value
				var cache = this.widget.cacheView;
				var context = this.getParent();
				var type = STYLE;

				if (context.$Type == "SpecificationDataSheetRevision") {
					// handle push from data sheet revision
					var datasheet = cache.getNode(context.__Parent__);
					var product = cache.getNode(datasheet.__Parent__);

					if (product) {
						viewParams.containerSourceUrl = product.$URL;
						type = product.$Type;
					}
				} else {
					// C8-50541 Type dropdown should match applicable product types of spec datasheet
					const specDefinition = context.$Type == "SpecificationSectionDefinition" ? context : cache.getNode(context.__Parent__);
					const applicableTypes = config.getSubtypeSetting(specDefinition.Subtype,"ApplicableForTypes");

					if (!applicableTypes.length) {
						// C8-50541 If no applicable types are in the SubtypeSettings
						// then all types are applicable
						viewParams.containerTypeStore = new Memory({
							data: [
								{id: STYLE, name: locale.type(STYLE)},
								{id: MATERIAL, name: locale.type(MATERIAL)},
								{id: FOOD, name: locale.type(FOOD)},
								{id: FORMULATED_MATERIAL, name: locale.type(FORMULATED_MATERIAL)},
								{id: PROJECT, name: locale.type(PROJECT)}
							]
						});
					}
					else {
						const data = applicableTypes.reduce((availableTypes, type) => {
							prodType = type.split(':')[1]; // type value in format "Spec.ApplicableForTypes:<Type>"
							availableTypes.push({id: prodType, name: locale.type(prodType)});
							return availableTypes;
						}, []);

						type = data[0]?.id; // first type in list of available types
						viewParams.containerTypeStore = new Memory({data});
					}
				}

				viewParams.containerSourceType = type;

				// to filter styles which type is not configure for spec data sheet from view
				var q = new Query();
				q.Or()
				.Attr("TDSMap", xml.OP_EQUALS, "true", "Child:ProductType/Child:ProductionConfig", "SpecificationDataSheet")
				.Attr("TDSList", xml.OP_EQUALS, "FoodTypeDataSheet:SpecificationDataSheet", "Child:Subtype/Child:Settings")
				.Attr("TDSList", xml.OP_EQUALS, "FormulatedMaterialTypeDataSheet:SpecificationDataSheet", "Child:Subtype/Child:Settings")
				.Attr("TDSList", xml.OP_EQUALS, "ProjectTypeDataSheet:SpecificationDataSheet", "Child:Subtype/Child:Settings");
				// assign query to containerQuery to prevent override by _setContainerTargetTypeAttr function in TypeSwitchboardTable.js
				viewParams.containerQuery = q;
			} else if (index == 1) {
				viewParams.onViewResults = function(qr, resultNodes) {
					if (qr.containsGroups()) {
						return;
					}

					// this action can be invoked from row-action (m_urlNode) or toolbar (m_urlParent)
					var urlSection = self.m_urlNode || self.m_urlParent;
					var ctx = self.getContext();
					var fd = self.getFormData();

					fd.m_bOmitSelections = true;

					_.each(resultNodes, function(datasheet) {
						var rev = ctx.cache.getNode(datasheet.CurrentRevision);
						if (rev) {
							var sharedSectionUrls = util.getDataSheetSharedSectionUrls(ctx, rev);

							if (_.contains(sharedSectionUrls, urlSection)) {
								fd.addAggregatedNode(datasheet);
							}
						}
					});
				}
			}
		},

		/**
		 * Issue request to push section to multiple products/datasheets
		 */
		process: function() {
			this.setProgress();

			var req = this.m_action.m_request;
			var dataSheets = this.m_formData.getSelectedNodes();
			var dataSheetUrls = [];
			var productUrls = [];

			_.each(dataSheets, function(node) {
				if (xml.isClientUrl(node.$URL)) {
					productUrls.push(node.__Parent__);
				} else {
					dataSheetUrls.push(node.$URL);
				}
			});

			// save for later in post-processing
			this._modifiedUrls = [].concat(dataSheetUrls).concat(productUrls);

			new PostRequest(req.m_module, req.m_operation)
				.addParameter(xml.PROP_URL, this.m_urlNode)
				.addParameter("TargetContainers", productUrls)
				.addParameter("TargetDataSheets", dataSheetUrls)
				.post({}).then(lang.hitch(this, this._onPublishResponse));
		},

		/**
		 * Remove modified nodes from cache. Since the request does not return the modified nodes, these nodes are now out of sync.
		 */
		postProcessSuccess: function() {
			_.each(this._modifiedUrls, this.m_cache.remove, this.m_cache);
		},

		/**
		 * Return data store containing selected products
		 * @return {Nodes}
		 */
		_getProductStore: function() {
			var products = this._fdProducts.getSelectedNodes();

			return new Nodes({
				data: products
			});
		}
	});
});